import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import React, { createContext, useContext, useState, useEffect } from 'react';
import { ZIndexLayers } from 'src/enums/zindex-layers.enum';

interface ModalOptions {
  allowCloseOnBackdropClick?: boolean;
}

interface Modal {
  component: React.ComponentType<any>;
  modalProps: any;
  allowCloseOnBackdropClick: boolean;
  key: number;
}

interface ModalContextData {
  openModal: <T>(component: React.ComponentType<T>, modalProps: T, options?: ModalOptions) => void;
  closeModal: () => void;
  clearAllModals: () => void;
  modalsLength: () => number;
  enableBackdropClose: boolean;
}

const ModalContext = createContext<ModalContextData | undefined>(undefined);

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [modals, setModals] = useState<Modal[]>([]);
  const [enableBackdropClose, setEnableBackdropClose] = useState(true);
  const [modalRoot, setModalRoot] = useState<HTMLElement | null>(null);
  const [readyToRender, setReadyToRender] = useState(false);
  const [allModalsClosed, setAllModalsClosed] = useState(true);

  const openModal = <T,>(
    component: React.ComponentType<T>,
    modalProps: T,
    openModalOptions?: ModalOptions,
  ) => {
    const { allowCloseOnBackdropClick = false } = openModalOptions || {};

    setModals((prevModals) => [
      ...prevModals,
      {
        component,
        modalProps,
        allowCloseOnBackdropClick,
        key: Date.now(),
      },
    ]);
    setEnableBackdropClose(allowCloseOnBackdropClick);
    setAllModalsClosed(false);
  };

  const closeModal = () => {
    setModals((prevModals) => prevModals.slice(0, -1));
  };

  const clearAllModals = () => {
    setModals([]);
  };

  useEffect(() => {
    if (typeof document !== 'undefined') {
      const modalRootElement = document.getElementById('modal-root');
      if (modalRootElement) {
        modalRootElement.style.zIndex = `${ZIndexLayers.MODAL}`;
        modalRootElement.style.position = 'relative';
        setModalRoot(modalRootElement);
        setReadyToRender(true);
      } else {
        console.error('### Modal root element not found');
      }
    }
  }, []);

  useEffect(() => {
    return () => {
      if (modalRoot) {
        modalRoot.innerHTML = '';
      }
    };
  }, [modalRoot]);

  const topModal = modals.length > 0 ? modals[modals.length - 1] : null;

  const modalElement = topModal ? (
    <motion.div
      key={topModal.key}
      initial={{ y: 40, opacity: 0 }}
      animate={{
        y: 0,
        opacity: 1,
        transition: { delay: 0.2, type: 'easeIn', duration: 0.2 },
      }}
      exit={{
        y: -40,
        opacity: 0,
        transition: { type: 'easeOut', duration: 0.2 },
      }}
      className="w-full h-full flex flex-col justify-center items-center p-4 md:p-8"
    >
      <topModal.component {...topModal.modalProps} />
    </motion.div>
  ) : null;

  const modalsLength = () => modals?.length;

  return (
    <ModalContext.Provider
      value={{
        openModal,
        closeModal,
        clearAllModals,
        enableBackdropClose,
        modalsLength,
      }}
    >
      {readyToRender &&
        modalRoot &&
        createPortal(
          <AnimatePresence>
            {!allModalsClosed && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{
                  opacity: 1,
                  transition: { type: 'easeIn', duration: 0.2 },
                }}
                exit={{
                  opacity: 0,
                  transition: { type: 'easeOut', duration: 0.2 },
                }}
                className={`w-full h-full fixed inset-0 overflow-auto bg-gray-600 bg-opacity-40 backdrop-blur-sm flex justify-center items-center no-scrollbar`}
                onClick={(e) => {
                  if (enableBackdropClose && e.target !== e.currentTarget) {
                    closeModal();
                  }
                }}
              >
                <AnimatePresence
                  mode="wait"
                  onExitComplete={() => setAllModalsClosed(modals.length === 0)}
                >
                  {modalElement}
                </AnimatePresence>
              </motion.div>
            )}
          </AnimatePresence>,
          modalRoot,
        )}
      {children}
    </ModalContext.Provider>
  );
}

export function useModal(): ModalContextData {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
}
